/**
 * API service layer for Fact Checker backend
 */

import axios, { AxiosError } from 'axios'
import type {
  User,
  VerificationResult,
  VerificationListItem,
  PaginationParams,
  HumanValidationInput,
  ApiError,
  VisibilityResponse,
  SearchSourcesResult,
  FavouriteToggleResponse,
  TagResponse,
  TagListResponse,
  ResultMetaResponse,
  SnapshotData,
} from '../types'

const api = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies for session auth
})

// Error handler
const handleError = (error: unknown): never => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiError>
    throw new Error(axiosError.response?.data?.detail || axiosError.message)
  }
  throw error
}

// ============================================================================
// Authentication
// ============================================================================

export const authApi = {
  /**
   * Get current authenticated user
   */
  getCurrentUser: async (): Promise<User> => {
    try {
      const response = await axios.get('/auth/me', { withCredentials: true })
      return response.data
    } catch (error) {
      throw handleError(error)
    }
  },

  /**
   * Initiate login flow
   */
  login: (organizationId?: string, redirectTo?: string): void => {
    const params = new URLSearchParams()
    if (organizationId) params.append('organization_id', organizationId)
    if (redirectTo) params.append('redirect_to', redirectTo)

    const url = `/auth/login${params.toString() ? '?' + params.toString() : ''}`
    window.location.href = url
  },

  /**
   * Logout current user
   */
  logout: async (): Promise<void> => {
    try {
      await axios.post('/auth/logout', {}, { withCredentials: true })
      window.location.href = '/'
    } catch (error) {
      throw handleError(error)
    }
  },

  /**
   * Accept terms of service
   */
  acceptTerms: async (termsVersion: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.post('/terms/accept', { terms_version: termsVersion })
      return response.data
    } catch (error) {
      throw handleError(error)
    }
  },

  /**
   * Mark onboarding as completed
   */
  completeOnboarding: async (): Promise<{ success: boolean }> => {
    try {
      const response = await api.post('/onboarding/complete')
      return response.data
    } catch (error) {
      throw handleError(error)
    }
  },
}

// ============================================================================
// Fact Verification Results
// ============================================================================

export const resultsApi = {
  /**
   * List verification results with pagination and optional search
   */
  listResults: async (
    params: Partial<PaginationParams> & {
      q?: string
      status?: string
      visibility?: string
      human_validated?: boolean
      favourites_only?: boolean
      tag?: string
      project?: string
    } = {}
  ): Promise<{
    results: VerificationListItem[]
    pagination: PaginationParams
  }> => {
    try {
      const response = await api.get('/results', { params })
      return response.data
    } catch (error) {
      throw handleError(error)
    }
  },

  /**
   * Search public verification results (no auth required)
   */
  searchPublicResults: async (
    params: { q?: string; limit?: number; offset?: number; status?: string } = {}
  ): Promise<{
    results: VerificationListItem[]
    pagination: PaginationParams
  }> => {
    try {
      const response = await api.get('/public/results', { params })
      return response.data
    } catch (error) {
      throw handleError(error)
    }
  },

  /**
   * Get detailed verification result
   */
  getResult: async (resultId: string): Promise<VerificationResult> => {
    try {
      const response = await api.get(`/results/${resultId}`)
      return response.data
    } catch (error) {
      throw handleError(error)
    }
  },

  /**
   * Add human validation override for a fact
   */
  addHumanValidation: async (
    resultId: string,
    validation: HumanValidationInput
  ): Promise<{ message: string; fact_key: string }> => {
    try {
      const response = await api.post(`/results/${resultId}/validate`, validation)
      return response.data
    } catch (error) {
      throw handleError(error)
    }
  },

  /**
   * Get favourite/tag metadata for a single result
   */
  getResultMeta: async (resultId: string): Promise<ResultMetaResponse> => {
    try {
      const response = await api.get(`/results/${resultId}/meta`)
      return response.data
    } catch (error) {
      throw handleError(error)
    }
  },

  /**
   * Get the immutable content snapshot a fact's verdict was reached against (#84)
   */
  getFactSnapshot: async (resultId: string, factKey: string): Promise<SnapshotData> => {
    try {
      const response = await api.get(
        `/results/${resultId}/facts/${encodeURIComponent(factKey)}/snapshot`
      )
      return response.data
    } catch (error) {
      throw handleError(error)
    }
  },

  /**
   * Set visibility (public/private) for a result
   */
  setVisibility: async (resultId: string, isPublic: boolean): Promise<VisibilityResponse> => {
    try {
      const response = await api.post(`/results/${resultId}/visibility`, {
        is_public: isPublic,
      })
      return response.data
    } catch (error) {
      throw handleError(error)
    }
  },

  /**
   * Provide source content manually when automated fetch fails.
   * Content is cached by URL and used for all verifications of that URL.
   */
  provideSourceContent: async (
    url: string,
    options: {
      file?: File
      textContent?: string
    }
  ): Promise<{
    success: boolean
    message: string
    source_id?: string
    url: string
    text_preview: string
    word_count: number
    needs_confirmation: boolean
  }> => {
    try {
      const formData = new FormData()

      if (options.file) {
        formData.append('file', options.file)
      } else if (options.textContent) {
        // Create a text file from pasted content
        const blob = new Blob([options.textContent], { type: 'text/plain' })
        const file = new File([blob], 'pasted-content.txt', { type: 'text/plain' })
        formData.append('file', file)
      }

      const response = await api.post(`/sources?url=${encodeURIComponent(url)}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response.data
    } catch (error) {
      throw handleError(error)
    }
  },

  /**
   * Re-run verification for a single fact (e.g. after providing source content).
   */
  reverifyFact: async (resultId: string, factKey: string): Promise<void> => {
    try {
      await api.post(`/results/${resultId}/facts/${encodeURIComponent(factKey)}/reverify`)
    } catch (error) {
      throw handleError(error)
    }
  },
}

// ============================================================================
// Source Search
// ============================================================================

export const searchApi = {
  /**
   * Search for supporting sources for an existing fact result
   */
  searchSourcesForFact: async (resultId: string, factKey: string): Promise<SearchSourcesResult> => {
    try {
      const response = await api.post(`/results/${resultId}/facts/${factKey}/search-sources`)
      return response.data
    } catch (error) {
      throw handleError(error)
    }
  },

  /**
   * Standalone search for supporting sources for a claim
   */
  searchSources: async (fact: string, excludeUrls?: string[]): Promise<SearchSourcesResult> => {
    try {
      const response = await api.post('/search-sources', {
        fact,
        exclude_urls: excludeUrls || [],
      })
      return response.data
    } catch (error) {
      throw handleError(error)
    }
  },
}

// ============================================================================
// Document Upload & Processing
// ============================================================================

export const documentApi = {
  /**
   * Parse a document and extract text/metadata
   */
  parseDocument: async (
    file: File
  ): Promise<{
    success: boolean
    message: string
    document_id: string
    document_hash: string
    extracted_text: string
    text_preview: string
    metadata: {
      filename: string
      file_size: number
      content_type: string
      page_count?: number
    }
  }> => {
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await api.post('/parse-document', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response.data
    } catch (error) {
      throw handleError(error)
    }
  },

  /**
   * Extract claims from document and verify them
   */
  factCheckDocument: async (
    file: File
  ): Promise<{
    status: string
    message?: string
    document_info: {
      filename: string
      char_count: number
      word_count: number
      extraction_method: string
    }
    claims_extracted?: number
    claims_with_citations?: Array<{
      claim: string
      citation: string
      page_number?: number
    }>
    verification_results?: Record<
      string,
      {
        claim: string
        citation: string
        is_valid: boolean
        validation_score: number
        reasoning: string
        source_accessible: boolean
      }
    >
    display_url?: string
    display_id?: string
  }> => {
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await api.post('/fact-check-document', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response.data
    } catch (error) {
      throw handleError(error)
    }
  },
}

// ============================================================================
// Admin
// ============================================================================

export interface AdminUser {
  user_id: string
  email: string
  display_name: string | null
  status: string
  organization_id: string | null
  created_at: string
}

export const adminApi = {
  /**
   * Check if current user is an admin
   */
  checkAdminStatus: async (): Promise<{ is_admin: boolean }> => {
    try {
      const response = await api.get('/admin/check')
      return response.data
    } catch {
      return { is_admin: false }
    }
  },

  /**
   * List users (admin only)
   */
  listUsers: async (statusFilter?: string): Promise<{ users: AdminUser[] }> => {
    try {
      const params = statusFilter ? { status_filter: statusFilter } : {}
      const response = await api.get('/admin/users', { params })
      return response.data
    } catch (error) {
      throw handleError(error)
    }
  },

  /**
   * Approve a user (admin only)
   */
  approveUser: async (
    userId: string
  ): Promise<{ success: boolean; message: string; email_sent: boolean }> => {
    try {
      const response = await api.post(`/admin/users/${userId}/approve`)
      return response.data
    } catch (error) {
      throw handleError(error)
    }
  },
}

// ============================================================================
// Feedback
// ============================================================================

export interface FeedbackInput {
  feedback_type: 'issue' | 'reverify' | 'general'
  result_id?: string
  email: string
  message: string
}

export interface FeedbackResponse {
  success: boolean
  message: string
}

export const feedbackApi = {
  /**
   * Submit feedback via email
   */
  submitFeedback: async (feedback: FeedbackInput): Promise<FeedbackResponse> => {
    try {
      const response = await api.post('/feedback', feedback)
      return response.data
    } catch (error) {
      throw handleError(error)
    }
  },
}

// ============================================================================
// Favourites
// ============================================================================

export const favouritesApi = {
  /**
   * Toggle favourite status for a result (add or remove)
   */
  toggleFavourite: async (resultId: string): Promise<FavouriteToggleResponse> => {
    try {
      const response = await api.post(`/results/${resultId}/favourite`)
      return response.data
    } catch (error) {
      throw handleError(error)
    }
  },
}

// ============================================================================
// Tags
// ============================================================================

export const tagsApi = {
  /**
   * Add a tag to a result (idempotent)
   */
  addTag: async (resultId: string, tagName: string): Promise<TagResponse> => {
    try {
      const response = await api.post(`/results/${resultId}/tags`, { tag_name: tagName })
      return response.data
    } catch (error) {
      throw handleError(error)
    }
  },

  /**
   * Remove a tag from a result
   */
  removeTag: async (resultId: string, tagName: string): Promise<TagResponse> => {
    try {
      const response = await api.delete(`/results/${resultId}/tags/${encodeURIComponent(tagName)}`)
      return response.data
    } catch (error) {
      throw handleError(error)
    }
  },

  /**
   * List all distinct tags used by current user (for autocomplete)
   */
  listUserTags: async (): Promise<TagListResponse> => {
    try {
      const response = await api.get('/tags')
      return response.data
    } catch (error) {
      throw handleError(error)
    }
  },
}

export interface ProjectItem {
  id: string
  name: string
  description?: string | null
  created_at: string
  result_count: number
}

export const projectsApi = {
  /**
   * List projects in the active organization (for the results-page filter).
   * Projects are auto-created when a verification names one (MCP tools).
   */
  listProjects: async (): Promise<{ projects: ProjectItem[] }> => {
    try {
      const response = await api.get('/projects')
      return response.data
    } catch (error) {
      throw handleError(error)
    }
  },
}

export default api
